import { DeviceKinds as WelderKinds } from '@sb/integrations/implementations/WeldMachine/shared/types/DeviceKinds';
import type { EquipmentItem } from '@sb/integrations/types';

import { getPermittedChildStepKindsShared } from '../../step-rules/weld';
import { type ValidationContext, type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { assertNotExamplePlaceholder } from '../../util/examplePlaceholder';
import { isUnsetEquipmentRef } from '../../util/unsetEquipmentRef';

import { CanonicalStepRuleBase, stepKindDisplayName } from './base';
import type { CanonicalValidateArgs } from './base';

type WeldArgs = {
  argumentKind: 'Weld';
  selectedMachineID?: string | null;
  selectedTorchID?: string | null;
  stitching?: { enabled: boolean };
  weaving?: { enabled: boolean };
};

const WELDER_KINDS = new Set<string>(WelderKinds);

function isWelder(item: EquipmentItem): boolean {
  return WELDER_KINDS.has(item.config.kind);
}

// Any CustomGripper qualifies as a torch, matching the Weld wizard's torch
// dropdown and the import sanitizer in `checkEquipmentReferences`.
function isTorch(item: EquipmentItem): boolean {
  return item.config.kind === 'CustomGripper';
}

class WeldRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.Weld', 'Weld');
  }

  protected validate({ args, children, ctx }: CanonicalValidateArgs): void {
    const a = args as unknown as WeldArgs;
    this.checkMachine(a, ctx);
    this.checkTorch(a, ctx);
    this.checkStitchingAndWeaving(a);
    this.checkChildStepKinds(children, ctx);
    this.checkStitchingMinWaypoints(a, children);
  }

  /**
   * Ensure a weld machine is selected, present on the robot, and enabled.
   *
   * @throws {ActionRequiredError}
   */
  private checkMachine(args: WeldArgs, ctx: ValidationContext): void {
    if (isUnsetEquipmentRef(args.selectedMachineID)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Must set a weld machine.',
        fieldId: 'selectedMachineID',
      });
    }

    this.checkEquipmentRef(ctx, args.selectedMachineID, isWelder, {
      fieldId: 'selectedMachineID',
      missing: 'Configured welder is not present on this robot.',
      disabled:
        'Configured welder is disabled — enable it in the Equipment Manager before running.',
    });
  }

  /**
   * Ensure a torch is selected, present on the robot, and enabled.
   *
   * @throws {ActionRequiredError}
   */
  private checkTorch(args: WeldArgs, ctx: ValidationContext): void {
    if (isUnsetEquipmentRef(args.selectedTorchID)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Must set a torch.',
        fieldId: 'selectedTorchID',
      });
    }

    this.checkEquipmentRef(ctx, args.selectedTorchID, isTorch, {
      fieldId: 'selectedTorchID',
      missing: 'Configured torch is not present on this robot.',
      disabled:
        'Configured torch is disabled — enable it in the Equipment Manager before running.',
    });
  }

  /**
   * Verify a referenced equipment id resolves to an enabled item of the
   * expected kind. A missing/wrong-kind reference is a blocking error; a
   * present-but-disabled reference is also blocking — the routine cannot run
   * the step until the device is re-enabled (or the step is suppressed, in
   * which case the rule engine skips this rule entirely).
   *
   * @throws {ActionRequiredError}
   */
  private checkEquipmentRef(
    ctx: ValidationContext,
    id: string,
    predicate: (item: EquipmentItem) => boolean,
    labels: { fieldId: string; missing: string; disabled: string },
  ): void {
    // Before the lookup: a copied example placeholder is not a reference to
    // equipment that was removed, and must not be reported as one.
    assertNotExamplePlaceholder(id, labels.fieldId);

    const existing = this.findEquipment(ctx, id);

    if (!existing || !predicate(existing)) {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: labels.missing,
        fieldId: labels.fieldId,
      });
    }

    if (!existing.isEnabled) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: labels.disabled,
        fieldId: labels.fieldId,
      });
    }
  }

  /**
   * Ensure stitching and weaving are not both enabled.
   *
   * @throws {ActionRequiredError}
   */
  private checkStitchingAndWeaving(args: WeldArgs): void {
    if (args.stitching?.enabled && args.weaving?.enabled) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Stitching and weaving cannot be enabled at the same time.',
      });
    }
  }

  /**
   * Ensure all child steps are permitted kinds (Waypoint, and Arc when the
   * `disableArcStepInWeld` flag is off).
   *
   * @throws {ActionRequiredError}
   */
  private checkChildStepKinds(
    children: Array<{ id: string; stepKind: string }>,
    ctx: ValidationContext,
  ): void {
    const permitted = getPermittedChildStepKindsShared(ctx.featureFlags ?? {});

    const badChild = children.find(
      (c) => !permitted.includes(c.stepKind as never),
    );

    if (badChild) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `Weld steps may not contain ${stepKindDisplayName(
          badChild.stepKind,
        )} steps.`,
      });
    }
  }

  /**
   * Ensure at least two child steps when stitching is enabled.
   *
   * @throws {ActionRequiredError}
   */
  private checkStitchingMinWaypoints(
    args: WeldArgs,
    children: Array<{ id: string; stepKind: string }>,
  ): void {
    if (args.stitching?.enabled && children.length < 2) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Weld steps must contain at least two waypoints when stitching is enabled.',
      });
    }
  }
}

export const WeldValidation: ValidationRule = new WeldRule().toValidationRule();
